import { useCallback } from 'react';

import { GridCellKind, getMiddleCenterBias } from '@glideapps/glide-data-grid';

import type { CustomRenderer } from '@glideapps/glide-data-grid';

import { CustomCellEditor, CustomCellProps as Props } from '../types';

import { upperFirst } from 'lodash';

import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';

import { Menu, Item } from '@/components/Menu';

import { isCustomCellOfKind } from '../utils';

import { getAllowedValue, validatePastedValue } from './utils';

import type { MenuCell } from './types';

import { kind } from './types';

const Editor: CustomCellEditor<MenuCell> = ({
  value: cell,
  onFinishedEditing,
}: Props<MenuCell>) => {
  const value = cell.data.value;
  const selectedKeys = value ? [value] : undefined;

  const handleAction = useCallback(
    (key: number | string | null) => {
      const value = String(key);
      onFinishedEditing({
        ...cell,
        data: {
          ...cell.data,
          value,
          error: null,
        },
      });
    },
    [onFinishedEditing, cell]
  );

  return (
    <div className="grid items-center h-[53px] px-x0_75">
      <Menu
        label={
          <button
            aria-label="More Options"
            autoFocus={true}
            className="grid grid-cols-[auto_max-content] justify-between items-center pl-x1"
          >
            <span>{upperFirst(value ?? '')}</span>
            <ChevronDownSmallSolid className="fill-legacy-mediumgray" />
          </button>
        }
        defaultOpen={true}
        placement="bottom start"
        selectedKeys={selectedKeys}
        selectionMode="single"
        onAction={handleAction}
      >
        <Item textValue="Buy" key="buy">
          <p className="text-xs text-legacy-darkergray m-0">Buy</p>
        </Item>
        <Item textValue="Sell" key="sell">
          <p className="text-xs text-legacy-darkergray m-0">Sell</p>
        </Item>
      </Menu>
    </div>
  );
};

export const Render: CustomRenderer<MenuCell> = {
  kind: GridCellKind.Custom,
  isMatch: isCustomCellOfKind(kind),
  draw: (args, cell) => {
    const { ctx, theme, rect } = args;
    const { value, error } = cell.data;

    const isError = error !== null;

    if (isError) {
      ctx.beginPath();
      ctx.fillStyle = 'rgba(230, 65, 65, 0.1)';
      ctx.rect(rect.x + 1, rect.y + 1, rect.width, rect.height - 1);
      ctx.fill();
      ctx.fillStyle = 'rgba(230, 65, 65, 1)';
      ctx.fillText(
        error,
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
      );
    } else {
      const text = upperFirst(getAllowedValue(cell.data.allowedValues, value));
      ctx.fillStyle = theme.textDark;
      ctx.fillText(
        text,
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
      );
    }
    return true;
  },
  measure: (ctx, cell, theme) => {
    const { value } = cell.data;
    return (
      (value ? ctx.measureText(value).width : 0) +
      theme.cellHorizontalPadding * 2
    );
  },
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
  }),
  onPaste: validatePastedValue,
};
